'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface SupportActionsProps {
  orderId: string;
  canModify: boolean;
}

const SupportActions = ({ orderId, canModify }: SupportActionsProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [showModifyOptions, setShowModifyOptions] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleContactSupport = () => {
    if (isHydrated && typeof window !== 'undefined') {
      window.location.href = 'tel:+911234567890';
    }
  };

  if (!isHydrated) {
    return (
      <div className="bg-card rounded-lg shadow-elevation-md p-6">
        <div className="animate-pulse space-y-3">
          <div className="h-12 bg-muted rounded"></div>
          <div className="h-12 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-elevation-md p-6">
      <h2 className="font-heading text-xl font-semibold text-foreground mb-4">
        Need Help?
      </h2>

      <div className="space-y-3">
        <button
          onClick={handleContactSupport}
          className="w-full flex items-center justify-between p-4 bg-primary text-primary-foreground rounded-lg transition-smooth hover:shadow-elevation-md hover-lift"
        >
          <div className="flex items-center gap-3">
            <Icon name="PhoneIcon" size={20} />
            <div className="text-left">
              <p className="font-medium text-sm">Contact Support</p>
              <p className="text-xs opacity-80">Available 24/7</p>
            </div>
          </div>
          <Icon name="ChevronRightIcon" size={20} />
        </button>

        {canModify && (
          <>
            <button
              onClick={() => setShowModifyOptions(!showModifyOptions)}
              className="w-full flex items-center justify-between p-4 bg-muted text-foreground rounded-lg transition-smooth hover:bg-border"
            >
              <div className="flex items-center gap-3">
                <Icon name="PencilSquareIcon" size={20} className="text-primary" />
                <div className="text-left">
                  <p className="font-medium text-sm">Modify Order</p>
                  <p className="text-xs text-muted-foreground">Change address or time</p>
                </div>
              </div>
              <Icon
                name="ChevronDownIcon"
                size={20}
                className={`transition-smooth ${showModifyOptions ? 'rotate-180' : ''}`}
              />
            </button>

            {showModifyOptions && (
              <div className="pl-4 space-y-2 animate-slide-down">
                <button className="w-full flex items-center gap-3 p-3 bg-background rounded-lg text-left transition-smooth hover:bg-muted">
                  <Icon name="MapPinIcon" size={18} className="text-primary" />
                  <span className="text-sm text-foreground">Change Delivery Address</span>
                </button>
                <button className="w-full flex items-center gap-3 p-3 bg-background rounded-lg text-left transition-smooth hover:bg-muted">
                  <Icon name="ClockIcon" size={18} className="text-primary" />
                  <span className="text-sm text-foreground">Reschedule Delivery</span>
                </button>
              </div>
            )}
          </>
        )}

        <button className="w-full flex items-center justify-between p-4 bg-muted text-foreground rounded-lg transition-smooth hover:bg-border">
          <div className="flex items-center gap-3">
            <Icon name="ChatBubbleLeftRightIcon" size={20} className="text-primary" />
            <div className="text-left">
              <p className="font-medium text-sm">Live Chat</p>
              <p className="text-xs text-muted-foreground">Chat with our team</p>
            </div>
          </div>
          <Icon name="ChevronRightIcon" size={20} />
        </button>

        <button className="w-full flex items-center justify-between p-4 bg-muted text-foreground rounded-lg transition-smooth hover:bg-border">
          <div className="flex items-center gap-3">
            <Icon name="QuestionMarkCircleIcon" size={20} className="text-primary" />
            <div className="text-left">
              <p className="font-medium text-sm">FAQs</p>
              <p className="text-xs text-muted-foreground">Find quick answers</p>
            </div>
          </div>
          <Icon name="ChevronRightIcon" size={20} />
        </button>
      </div>
    </div>
  );
};

export default SupportActions;